import { useEffect, useMemo, useState } from "react";

import { useMatrix, useMatrixRooms } from "~/context/MatrixContext";
import { getCachedMember, getMemberLoadState, loadMembers, type RoomMember } from "~/lib/matrix";

export function useOwnMemberNames(enabled: boolean): { names: string[]; pending: boolean } {
  const { client, userId } = useMatrix();
  const rooms = useMatrixRooms();
  const [loaded, setLoaded] = useState(0);
  useEffect(() => {
    if (!enabled || !client || !userId) return;
    let live = true;
    const idle = rooms
      .filter((room) => !room.isSpace && getMemberLoadState(client, room.id) === "idle")
      .slice(0, 5);
    for (const room of idle) {
      loadMembers(client, room.id)
        .then(() => live && setLoaded((n) => n + 1))
        .catch(() => undefined);
    }
    return () => {
      live = false;
    };
  }, [enabled, client, userId, rooms]);
  return useMemo(() => {
    if (!enabled || !client || !userId) return { names: [], pending: false };
    const names: string[] = [];
    let pending = false;
    for (const room of rooms) {
      if (room.isSpace) continue;
      let self: RoomMember | null;
      try {
        self = getCachedMember(client, room.id, userId);
      } catch {
        continue;
      }
      if (getMemberLoadState(client, room.id) === "loading") pending = true;
      if (self?.displayName) names.push(self.displayName);
    }
    return { names, pending };
  }, [enabled, client, userId, rooms, loaded]);
}
